import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, CopyPlus, X, Info } from "lucide-react";
import { aiApi } from "./aiApi";
import AIWordEditorToolbar from "./AIWordEditorToolbar";
import { buildPara, serialize, resolve, hostOf, applyFmt, setAlign } from "./wordEditorDom";

const fileIdOf = (url) => (url || "").match(/\/ai\/files\/([0-9a-f-]{36})/)?.[1];

function renderBlocks(root, blocks, dflt) {
  blocks.forEach((b) => {
    if (b.type === "p") { root.appendChild(buildPara(b, dflt)); return; }
    const table = document.createElement("table");
    b.rows.forEach((row) => {
      const tr = table.insertRow();
      row.forEach((cell) => {
        const td = tr.insertCell();
        td.colSpan = cell.span || 1;
        renderBlocks(td, cell.blocks, dflt);
      });
    });
    root.appendChild(table);
  });
}

/** Penyunting Word dalam app: tebal/condong/garis bawah, fon, saiz, warna, sorotan & jajaran - format lain kekal asal. */
export default function AIWordEditor({ file, onSaved, onCancel }) {
  const pageRef = useRef(null);
  const rangeRef = useRef(null);
  const hostRef = useRef(null);
  const origRef = useRef({});
  const undoRef = useRef([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [fmt, setFmt] = useState(null);
  const [align, setAlignState] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [undoN, setUndoN] = useState(0);
  const [saving, setSaving] = useState("");
  const fid = fileIdOf(file?.url);

  useEffect(() => {
    if (!fid) { setErr("Fail tidak sah untuk disunting"); return; }
    aiApi.wordEditor(fid).then(setData).catch((e) => setErr(e.response?.data?.detail || "Dokumen tidak dapat dibuka untuk disunting"));
  }, [fid]);

  useEffect(() => {
    const root = pageRef.current;
    if (!data || !root) return;
    root.innerHTML = "";
    const dflt = { b: false, i: false, u: false, sz: data.default_size, f: data.default_font, c: "000000", h: null };
    renderBlocks(root, data.blocks, dflt);
    origRef.current = {};
    root.querySelectorAll('[data-pid][contenteditable="true"]').forEach((h) => { origRef.current[h.dataset.pid] = JSON.stringify(serialize(h)); });
  }, [data]);

  const syncState = useCallback(() => {
    const sel = window.getSelection();
    const root = pageRef.current;
    if (!sel?.rangeCount || !root) return;
    const range = sel.getRangeAt(0);
    const host = hostOf(range.startContainer, root);
    if (!host) return;
    rangeRef.current = range.cloneRange();
    hostRef.current = host;
    const node = range.startContainer.nodeType === 3 ? range.startContainer : host.querySelector("span[data-r]")?.firstChild || host;
    setFmt(resolve(node, host));
    setAlignState(host.dataset.align);
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", syncState);
    return () => document.removeEventListener("selectionchange", syncState);
  }, [syncState]);

  const pushUndo = (host) => { undoRef.current.push({ host, html: host.innerHTML, align: host.dataset.align }); setUndoN(undoRef.current.length); };

  const doFmt = (key, value) => {
    const range = rangeRef.current;
    const host = range && hostOf(range.commonAncestorContainer, pageRef.current);
    if (!range || range.collapsed || !host) { toast.info("Pilih (highlight) teks dalam satu perenggan dahulu"); return; }
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    pushUndo(host);
    if (applyFmt(range, pageRef.current, key, value)) { setDirty(true); syncState(); }
  };

  const doAlign = (a) => {
    const host = hostRef.current;
    if (!host) { toast.info("Klik pada perenggan dahulu"); return; }
    pushUndo(host);
    setAlign(host, a);
    setAlignState(a);
    setDirty(true);
  };

  const undo = () => {
    const u = undoRef.current.pop();
    setUndoN(undoRef.current.length);
    if (!u) return;
    u.host.innerHTML = u.html;
    setAlign(u.host, u.align);
    setDirty(true);
  };

  const onKeyDown = (e) => {
    const k = e.key.toLowerCase();
    if ((e.ctrlKey || e.metaKey) && ["b", "i", "u"].includes(k)) { e.preventDefault(); doFmt(k); return; }
    if (e.key === "Enter") { e.preventDefault(); document.execCommand("insertText", false, "\n"); }
  };
  const onPaste = (e) => { e.preventDefault(); document.execCommand("insertText", false, e.clipboardData.getData("text/plain")); };

  const save = async (mode) => {
    const hosts = [...pageRef.current.querySelectorAll('[data-pid][contenteditable="true"]')];
    const paragraphs = hosts.map(serialize).filter((p) => JSON.stringify(p) !== origRef.current[p.pid]);
    if (!paragraphs.length) { toast.info("Tiada perubahan untuk disimpan"); return; }
    setSaving(mode);
    try {
      const r = await aiApi.wordEditorSave(fid, { paragraphs, mode, base_version: data.version });
      toast.success(r.mode === "replace" ? `Disimpan (versi ${r.version}) - ${r.changed} perenggan diubah` : `Salinan baharu disimpan: ${r.filename}`);
      onSaved(r);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan suntingan"); } finally { setSaving(""); }
  };

  if (err) return <div className="text-sm text-rose-600 p-4" data-testid="ai-word-editor-error">{err}</div>;
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-2" data-testid="ai-word-editor">
      <AIWordEditorToolbar fmt={fmt} align={align} fonts={data?.fonts} onFmt={doFmt} onAlign={doAlign} onUndo={undo} canUndo={undoN > 0} />
      <div className="flex-1 overflow-y-auto rounded-2xl border border-slate-100 bg-slate-50 p-3">
        {!data && <div className="h-full grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
        <div ref={pageRef} onKeyDown={onKeyDown} onPaste={onPaste} onInput={() => setDirty(true)}
          className={`wed-page mx-auto max-w-[700px] bg-white shadow-sm rounded-lg px-8 py-8 ${data ? "" : "hidden"}`} data-testid="ai-word-editor-page" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] text-slate-500 flex items-center gap-1 mr-auto">
          <Info className="h-3 w-3" />{data?.legacy_doc ? "Fail .doc lama - disimpan sebagai salinan .docx." : "Kepala surat, logo & gambar kekal asal."} Enter = baris baharu.
          {dirty && <b className="text-amber-600 ml-1" data-testid="ai-word-editor-dirty">Belum disimpan</b>}
        </span>
        <button type="button" onClick={onCancel} className="h-9 px-3 rounded-full border border-slate-200 bg-white text-xs font-bold text-slate-600 inline-flex items-center gap-1 hover:bg-slate-50 transition-colors" data-testid="ai-word-editor-cancel"><X className="h-3.5 w-3.5" />Tutup</button>
        <button type="button" onClick={() => save("copy")} disabled={!!saving || !data} className="h-9 px-3 rounded-full border border-sky-200 bg-sky-50 text-xs font-bold text-sky-700 inline-flex items-center gap-1 hover:bg-sky-100 transition-colors disabled:opacity-50" data-testid="ai-word-editor-save-copy">
          {saving === "copy" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CopyPlus className="h-3.5 w-3.5" />}Simpan salinan
        </button>
        {!data?.legacy_doc && (
          <button type="button" onClick={() => save("replace")} disabled={!!saving || !data} className="h-9 px-4 rounded-full bg-sky-600 text-white text-xs font-bold inline-flex items-center gap-1 hover:bg-sky-700 transition-colors disabled:opacity-50" data-testid="ai-word-editor-save">
            {saving === "replace" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}Simpan
          </button>
        )}
      </div>
    </div>
  );
}
